import { buildPaletteAlert, type ComponentAlertPalette } from "./alertPalette";
import colors from "./base.colors.dark";
import type lightPalette from "./palette.light.ts";

const componentAlert: ComponentAlertPalette = {
  info: {
    filledBg: colors.blue[800],
    filledColor: colors.blue[100],
    standardBg: colors.blue[900],
    standardColor: colors.blue[100],
  },
  success: {
    filledBg: colors.green[900],
    filledColor: colors.green[500],
    standardBg: colors.green[900],
    standardColor: colors.green[500],
  },
  warning: {
    filledBg: colors.orange[800],
    filledColor: colors.orange[300],
    standardBg: colors.orange[800],
    standardColor: colors.orange[300],
  },
  error: {
    filledBg: colors.red[900],
    filledColor: colors.red[500],
    standardBg: colors.red[900],
    standardColor: colors.red[500],
  },
};

const darkPalette: typeof lightPalette = {
  // palette values for dark mode
  primary: {
    main: colors.defy[500],
    light: colors.defy[300],
    lightest: colors.defy[100],
    dark: colors.defy[800],
    darkest: colors.defy[900],
  },
  secondary: {
    main: colors.defy[500],
    light: colors.defy[700],
  },
  neutral: {
    dark: colors.grey[950],
    main: colors.grey[600],
    light: colors.grey[400],
    lightest: colors.grey[100],
    contrastText: "#EAFFEA",
  },
  action: {
    active: colors.grey[300],
    hover: colors.grey[950],
    selected: colors.blue[800],
  },
  background: {
    default: colors.primary[800],
    light: colors.primary[400],
    paper: colors.blue[900],
  },
  text: {
    primary: colors.grey[300],
    secondary: colors.grey[500],
    light: colors.grey[300],
    dark: colors.grey[700],
    disabled: colors.grey[800],
  },
  grey: {
    light: colors.primary[900],
    fade: colors.grey[700],
    bold: colors.grey[200],
    dark: colors.grey[100],
    100: colors.grey[100],
    200: colors.grey[200],
    300: colors.grey[300],
    400: colors.grey[400],
    500: colors.grey[500],
    600: colors.grey[600],
    700: colors.grey[700],
    800: colors.grey[800],
    900: colors.grey[900],
    950: colors.grey[950],
    999: colors.grey[999],
  },
  // TODO need revision
  info: {
    lightest: colors.blue[100],
    light: colors.blue[400],
    main: colors.blue[600],
    dark: colors.blue[800],
    contrastText: colors.blue[500],
  },
  success: {
    lightest: colors.green[200],
    light: colors.green[400],
    main: colors.green[500],
    dark: colors.green[900],
    //contrastText: darkColors.green[900],
    contrastText: colors.green[700],
  },
  warning: {
    lightest: colors.orange[100],
    light: colors.orange[400],
    main: colors.orange[500],
    dark: colors.orange[800],
    contrastText: colors.orange[800],
  },
  error: {
    lightest: colors.red[200],
    light: colors.red[400],
    main: colors.red[500],
    dark: colors.red[800],
    contrastText: colors.red[800],
  },
  Alert: buildPaletteAlert(componentAlert),
  components: {
    MuiDataGrid: {
      rowHover: colors.blue[900],
    },
  },
  Chip: {
    defaultBorder: colors.grey[800],
  },
  table: {
    rowHover: colors.grey[950],
    selectedRow: colors.defy[900],
  },
  TableCell: {
    border: colors.grey[950],
  },
  DataGrid: {
    // Container background
    bg: "inherit",
    // Pinned rows and columns background
    // pinnedBg: '#f1f5f9',
    // Column header background
    headerBg: colors.primary[700],
  },
  LinearProgress: {
    successBg: colors.green[900],
    warningBg: colors.orange[900],
    errorBg: colors.red[900],
    infoBg: colors.blue[900],
    neutralBg: colors.grey[900],
  },
  runStatus: {
    unknown: colors.grey[800],
    disabled: colors.grey[600],
    running: colors.blue[600],
    success: colors.green[500],
    completed: colors.green[500],
    completed_with_retries: colors.green[500],
    with_retries: colors.green[700],
    preempted: colors.red[400],
    failed: colors.red[400],
    error: colors.red[400],
    completed_with_errors: colors.green[500],
    failed_with_errors: colors.red[400],
  },
  topbar: {
    background: colors.common.black,
  },
  sidebar: {
    text: colors.grey[100],
  },
  stackedBarChart: ["#cdb4db", "#ffc8dd", "#ffafcc", "#bde0fe", "#a2d2ff"],
  chartColorsByType: {
    cost: colors.defy[500],
    waste: colors.pink[400],
  },
  charts: {
    testLayer: colors.defy[800],
    palette: [
      colors.blue[600],
      colors.deepPurple[600],
      colors.orange[600],
      colors.cyan[600],
      colors.pink[600],
      colors.amber[600],
      colors.lime[600],
      colors.blueGrey[600],
      colors.teal[600],
      colors.lightGreen[600],
      colors.indigo[600],
      colors.yellow[400],
    ],
    sparkMetrics: {
      background: [
        colors.grey[600],
        colors.grey[700],
        colors.grey[500],
        colors.grey[800],
        colors.grey[600],
        colors.grey[700],
      ],
      overlay: [
        colors.defy[400],
        colors.defy[300],
        colors.defy[500],
        colors.defy[200],
        colors.defy[700],
      ],
      secondary: [
        colors.orange[400],
        colors.orange[600],
        colors.orange[400],
        colors.orange[600],
        colors.orange[400],
        colors.orange[600],
      ],
    },
    /** @see palette.light.ts — same grey steps, so the tokens invert with the mode. */
    timeline: {
      /** Bars carrying no status of their own — a finished run's executors. */
      defaultTone: {
        main: colors.grey[500],
        light: colors.grey[900],
      },
      /** Fill for bars that read as absence — idle executor cores. */
      idleBar: colors.grey[950],
      /** Edge of those bars: one step up from the fill. */
      idleBorder: colors.grey[900],
      /** Rule between timeline lines, and between stacked blocks. */
      separator: colors.grey[800],
      /** Plate behind a non-transparent timeline. */
      gantt: colors.grey[950],
    },
  },
} as const;

export default darkPalette;
